/**
 * The designed absence.
 *
 * This is a SIBLING of `CresultcardMedia`, not a prop of it, because the most
 * common failure is not a broken image — it is that there was never a URL at
 * all, and a fallback expressed as a prop of the renderer cannot cover that
 * case. Five of nineteen identity assets in one pillar family are permanently
 * unobtainable (a bot-challenged CDN answering 403, a host answering 401), so
 * absence is not an edge case here: it is the normal state for a quarter of two
 * pillars' rows, and it has to be good enough to ship as the normal state.
 *
 * It renders in the EXACT frame the asset would have occupied, so a failed
 * cover never changes a card's height and a grid never re-lays itself out.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import {
  CRC_MEDIA_PLATE_BACKGROUND_IMAGE,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  crcClampLines,
} from "./cresultcard.tokens";

/** Enough of the title to identify the item; past this it stops being a label. */
const FALLBACK_TITLE_LINES = 3;
const MONOGRAM_WORDS = 2;
const NON_WORD_PATTERN = /[^\p{L}\p{N}]+/u;

/** A dashed hairline, so a failure is never mistaken for a supplied monogram. */
const FALLBACK_DASHED_BORDER = "1px dashed rgba(255,255,255,0.16)";

interface CresultcardMediaFallbackProps {
  /** The item's own name — the best identity available once the asset is gone. */
  readonly title: string;
  /** A second line: an author, an artist, a publisher, captured dimensions. */
  readonly secondary?: string;
  /**
   * A designed absence that IS content — a date plate standing in for a poster
   * that was never captured. It replaces the title/secondary/reason stack and
   * owns the face, so the frame stops describing itself as an image: what is
   * inside it is real content with its own semantics.
   */
  readonly children?: ReactNode;
}

/** The first one or two significant words, as initials. */
export function cresultcardMonogram(name: string): string {
  return name
    .split(NON_WORD_PATTERN)
    .filter((word) => word.length > 0)
    .slice(0, MONOGRAM_WORDS)
    .map((word) => word.slice(0, 1).toUpperCase())
    .join("");
}

export function CresultcardMediaFallback({
  title,
  secondary,
  children,
}: CresultcardMediaFallbackProps): ReactElement {
  const hasContent = children != null;

  if (hasContent) {
    return (
      <span
        data-el="Cresultcard/MediaFallback/Root"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          minWidth: 0,
          borderRadius: "inherit",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label={title}
      data-el="Cresultcard/MediaFallback/Root"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-end",
        gap: 3,
        padding: 10,
        border: FALLBACK_DASHED_BORDER,
        borderRadius: "inherit",
        boxSizing: "border-box",
        backgroundImage: CRC_MEDIA_PLATE_BACKGROUND_IMAGE,
      }}
    >
      <span
        style={{
          ...crcClampLines(FALLBACK_TITLE_LINES),
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "-0.015em",
          lineHeight: 1.2,
          color: CRC_TEXT_MUTED,
        }}
      >
        {title}
      </span>
      {secondary != null && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 540,
            lineHeight: 1.2,
            color: CRC_TEXT_FAINT,
          }}
        >
          {secondary}
        </span>
      )}
    </span>
  );
}
